import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Camera, ScanSearch, MessageSquareText, ClipboardCheck, UserCheck, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ShareButton from "@/components/ShareButton";
import EmailVerifyNotice from "@/components/EmailVerifyNotice";
import { Disclaimer } from "@/components/Common";
import InstallHelp from "@/components/InstallHelp";

const HERO = "https://customer-assets-agu9un31.emergentagent.net/job_pianta-check/artifacts/u0nndf04_hero_smartphone_foglia_base_ottimizzata.webp";
const PRO_IMG = "https://customer-assets-agu9un31.emergentagent.net/job_pianta-check/artifacts/1xwyx0o4_foto-consulenza-leggera%20%281%29.webp";

const steps = [
  { icon: ScanSearch, t: "Riconoscimento", d: "La pianta viene riconosciuta e tu confermi la specie." },
  { icon: MessageSquareText, t: "Anamnesi guidata", d: "Solo le domande davvero utili per il tuo caso." },
  { icon: ClipboardCheck, t: "Analisi e indicazioni di cura", d: "Sintomi, possibili cause e indicazioni su come intervenire." },
  { icon: UserCheck, t: "Consulenza", d: "Se serve, il caso passa alla Dott.ssa De Vanna." },
];

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [installOpen, setInstallOpen] = useState(false);
  const start = () => {
    if (!user) return navigate("/accedi", { state: { from: "/analisi/nuova" } });
    if (!user.consents?.storage?.granted) return navigate("/consenso", { state: { next: "/analisi/nuova" } });
    navigate("/analisi/nuova");
  };

  return (
    <div data-testid="home-page" className="bg-[#f8f3e9]">
      <section className="relative min-h-[585px] overflow-hidden bg-[#0d2517] md:hidden" data-testid="home-hero">
        <img src={HERO} alt="Smartphone che fotografa una foglia con sintomo" className="pointer-events-none absolute max-w-none select-none" style={{ width: "121.5%", left: "-12%", top: "-70px" }} data-testid="home-hero-image" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(13,37,23,0.88) 0%, rgba(13,37,23,0.6) 42%, rgba(13,37,23,0.18) 72%, rgba(13,37,23,0.08) 100%)" }} />
        <div className="absolute inset-x-0 bottom-0 h-64" style={{ background: "linear-gradient(0deg, rgba(13,37,23,1) 0%, rgba(13,37,23,0.92) 40%, rgba(13,37,23,0) 100%)" }} />
        <div className="hero-text relative flex min-h-[585px] flex-col px-6 pb-8 pt-12 text-[#f8f3e9]">
          <p className="eyebrow fade-up max-w-[70%] !text-[#ddc69c]">Dott.ssa Luigia De Vanna · Dottore Agronomo</p>
          <h1 className="fade-up font-serif text-5xl font-semibold leading-[1.02]" data-testid="home-title">
            Medico delle<br /><span className="italic text-[#ddc69c]">Piante</span>
          </h1>
          <p className="fade-up mt-2 font-serif text-2xl font-normal tracking-wide text-[#f8f3e9]/85" data-testid="home-category-label">(Agrumi)</p>
          <p className="fade-up-2 mt-7 max-w-[52%] font-serif text-2xl leading-snug">Qualcosa non va nella tua pianta?</p>
          <p className="fade-up-2 mt-2 max-w-[50%] text-sm text-[#f8f3e9]/90">Fotografala e ricevi una prima analisi agronomica specialistica.</p>
          <div className="mt-auto pt-6">
            <button onClick={start} className="btn-hero fade-up-3 w-full py-4 text-sm tracking-[0.12em]" style={{ textShadow: "none" }} data-testid="start-analysis-button">
              <Camera size={18} /> FOTOGRAFA LA TUA PIANTA
            </button>
            <p className="fade-up-3 mt-3 text-center text-[13px] leading-snug text-[#f8f3e9]/90" style={{ textWrap: "balance" }} data-testid="home-growth-note">La tua app si arricchirà automaticamente di nuove specie vegetali.</p>
            <button type="button" onClick={() => setInstallOpen(true)} className="fade-up-3 mt-2 block w-full text-center text-[13px] leading-snug text-[#f8f3e9]/90 underline decoration-[#ddc69c]/50 underline-offset-4" style={{ textWrap: "balance" }} data-testid="home-install-note">Aggiungi <strong className="font-semibold text-[#ddc69c]">GRATIS</strong> Medico delle Piante alla schermata iniziale.</button>
          </div>
        </div>
      </section>

      <section className="relative hidden overflow-hidden bg-[#0d2517] md:block" data-testid="home-hero-desktop">
        <div className="relative mx-auto h-[560px] max-w-6xl lg:h-[640px]">
          <img src={HERO} alt="Smartphone che fotografa una foglia con sintomo" className="hero-desktop-img pointer-events-none absolute right-0 top-0 h-full w-auto max-w-none select-none" data-testid="home-hero-desktop-image" />
          <div className="absolute inset-y-0 right-0 w-[60%]" style={{ background: "linear-gradient(90deg, rgba(13,37,23,0.55) 0%, rgba(13,37,23,0.2) 45%, rgba(13,37,23,0.12) 100%)" }} />
          <div className="hero-text relative flex h-full max-w-[58%] flex-col justify-center px-10 text-[#f8f3e9] lg:max-w-[50%] lg:px-16">
            <p className="eyebrow fade-up !text-[#ddc69c]">Dott.ssa Luigia De Vanna · Dottore Agronomo</p>
            <h1 className="fade-up font-serif text-6xl font-semibold leading-[1.02] lg:text-7xl" data-testid="home-title-desktop">
              Medico delle<br /><span className="italic text-[#ddc69c]">Piante</span>
            </h1>
            <p className="fade-up mt-3 font-serif text-3xl font-normal tracking-wide text-[#f8f3e9]/85 lg:text-4xl" data-testid="home-category-label-desktop">(Agrumi)</p>
            <p className="fade-up-2 mt-9 font-serif text-3xl leading-snug">Qualcosa non va nella tua pianta?</p>
            <p className="fade-up-2 mt-3 text-base text-[#f8f3e9]/90">Fotografala e ricevi una prima analisi agronomica specialistica.</p>
            <div className="mt-10 self-start">
              <button onClick={start} className="btn-hero fade-up-3 whitespace-nowrap px-7 py-4 text-sm tracking-[0.12em] lg:px-10" style={{ textShadow: "none" }} data-testid="start-analysis-button-desktop">
                <Camera size={18} /> FOTOGRAFA LA TUA PIANTA
              </button>
              <p className="fade-up-3 mt-3 whitespace-nowrap text-center text-[11px] text-[#f8f3e9]/85 lg:text-xs" data-testid="home-growth-note-desktop">La tua app si arricchirà automaticamente di nuove specie vegetali.</p>
              <button type="button" onClick={() => setInstallOpen(true)} className="fade-up-3 mt-1.5 block w-full whitespace-nowrap text-center text-[11px] text-[#f8f3e9]/85 underline decoration-[#ddc69c]/50 underline-offset-4 lg:text-xs" data-testid="home-install-note-desktop">Aggiungi <strong className="font-semibold text-[#ddc69c]">GRATIS</strong> Medico delle Piante alla schermata iniziale.</button>
            </div>
          </div>
        </div>
      </section>

      <div className="space-y-8 px-5 py-8 md:mx-auto md:max-w-2xl md:py-12">
        <EmailVerifyNotice />
        <Disclaimer className="border-[#c9a96e] bg-[#f8f3e9]" />

        <section data-testid="how-it-works">
          <p className="eyebrow !text-[#c9a96e]">Come funziona</p>
          <h2 className="font-serif text-2xl font-semibold text-[#0d2517]">Dalla foto alla risposta</h2>
          <ol className="mt-5 space-y-4">
            {steps.map(({ icon: Icon, t, d }, i) => (
              <li key={t} className="flex gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#c9a96e] bg-[#ffffff] text-[#173522]">
                  <Icon size={18} strokeWidth={1.6} />
                </span>
                <div>
                  <p className="text-sm font-semibold text-[#0d2517]"><span className="mr-2 font-serif text-[#c9a96e]">0{i + 1}</span>{t}</p>
                  <p className="text-sm text-[#173522]/75">{d}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="overflow-hidden rounded-2xl border border-[#ddc69c] bg-[#ffffff] shadow-sm" data-testid="home-pro-card">
          <img src={PRO_IMG} alt="Dott.ssa Luigia De Vanna, agronoma, in campo tra gli agrumi" className="aspect-[16/9] w-full object-cover" />
          <div className="border-t-2 border-[#c9a96e] p-5">
            <p className="eyebrow !text-[#c9a96e]">Consulenza professionale</p>
            <p className="font-serif text-xl font-semibold text-[#0d2517]">Dott.ssa Luigia De Vanna</p>
            <p className="text-sm text-[#173522]/75">Dottore Agronomo. Quando l'analisi non basta, puoi inviarle il caso completo senza ripetere l'anamnesi.</p>
          </div>
        </section>

        {user && (
          <Link to="/piante" className="flex items-center justify-between rounded-2xl border border-[#c9a96e]/40 bg-[#173522] p-5 text-[#f8f3e9]" data-testid="home-my-plants-link">
            <div>
              <p className="eyebrow !text-[#ddc69c]">Area personale</p>
              <p className="font-serif text-xl">Le mie piante</p>
            </div>
            <ArrowRight className="text-[#ddc69c]" />
          </Link>
        )}
        <div className="flex justify-center"><ShareButton testid="home-share-button" /></div>
        <p className="text-center text-[11px] text-[#173522]/60">Altre categorie (orto, aromatiche, frutto, ornamentali, giardino mediterraneo) in arrivo.</p>
      </div>
      <InstallHelp open={installOpen} onOpenChange={setInstallOpen} />
    </div>
  );
}
